/**
 * Obstruction wizard — Attachments step (last section).
 *
 * Thin wrapper around the shared `AttachmentsFormSection`, wired to
 * `form.values.attachments` (new uploads) and, in edit mode, the obstruction's
 * already-persisted attachments with an optional delete action.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentsFormSection } from '@/components/common/AttachmentsFormSection';
import type { ExistingAttachmentItem } from '@/components/common/AttachmentsFormSection';
import type { UseFormReturnType } from '@mantine/form';

import type { ObstructionWizardFormState } from '../../types';

interface StepAttachmentsProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  isLoading: boolean;
  existingAttachments?: ExistingAttachmentItem[];
  onDeleteExistingAttachment?: (attachmentId: string) => void;
}

export const StepAttachments = ({
  form,
  isLoading,
  existingAttachments = [],
  onDeleteExistingAttachment,
}: StepAttachmentsProps) => {
  const { t } = useTranslation();

  const handleFilesChange = useCallback(
    (files: File[]) => {
      form.setFieldValue('attachments', files);
    },
    [form]
  );

  const attachmentsError =
    typeof form.errors.attachments === 'string'
      ? form.errors.attachments
      : undefined;

  return (
    <AttachmentsFormSection
      files={form.values.attachments}
      onFilesChange={handleFilesChange}
      existingAttachments={existingAttachments}
      onDeleteExistingAttachment={onDeleteExistingAttachment}
      isLoading={isLoading}
      title={t('obstructions.wizard.attachments.heading')}
      description={t('obstructions.wizard.attachments.description')}
      existingFilesLabel={t('obstructions.wizard.attachments.existingFiles', {
        count: existingAttachments.length,
      })}
      error={attachmentsError}
    />
  );
};
